import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";
import { shouldRefetchOnFocus } from "./useCachedQuery";
import { fromApi, type Integration } from "../types/integration";

export type IntegrationsStatus = "loading" | "ready" | "error";

export type RequestErrorCode =
  | "UNKNOWN_SLUG"
  | "INVALID_STATE"
  | "UNAUTHORIZED"
  | "UNKNOWN";

export type RequestResult =
  | { ok: true; integration: Integration }
  | { ok: false; code: RequestErrorCode; message: string };

export type UseIntegrationsResult = {
  status: IntegrationsStatus;
  integrations: ReadonlyArray<Integration>;
  error: string | null;
  request: (slug: string) => Promise<RequestResult>;
  // IP-152: fire-and-forget refresh used by the IntegrationDetail polling
  // loop while connectionStatus === 'pending'. Resolves once the fetch
  // settles so callers can chain follow-up logic; the underlying state
  // (integrations / status) updates as a side effect.
  refetch: () => Promise<void>;
};

type ServerError = { code: string; message: string };

const isRequestErrorCode = (code: string): code is RequestErrorCode =>
  code === "UNKNOWN_SLUG" ||
  code === "INVALID_STATE" ||
  code === "UNAUTHORIZED" ||
  code === "UNKNOWN";

// Availability/state is computed per user (admin gating, tenant state),
// so the cache is scoped by user email + tenant. The cached value is the
// already-mapped Integration[]; changing Integration/fromApi requires a
// cache version bump in dashboardCache.ts.
const integrationsResource = (tenantId: string | null): string =>
  `integrations:${tenantId ?? "catalog"}`;

export const useIntegrations = (
  tenantId: string | null,
): UseIntegrationsResult => {
  const resource = integrationsResource(tenantId);
  const cached = useMemo(
    () => readUserCache<ReadonlyArray<Integration>>(resource),
    [resource],
  );
  const [status, setStatus] = useState<IntegrationsStatus>(cached !== null ? "ready" : "loading");
  const [integrations, setIntegrations] = useState<ReadonlyArray<Integration>>(
    cached ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  // Stamped on every settled fetch so the focus revalidation below can throttle.
  const lastFetchAt = useRef<number>(0);

  // No tenant yet → fetch the catalog so the grid still renders with
  // default-available cards. Once a tenant exists, we get the same shape
  // merged with that tenant's state (status + connectionStatus from V29).
  const fetchIntegrations = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const url = tenantId === null
        ? "/api/integrations"
        : `/api/integrations/${encodeURIComponent(tenantId)}`;
      const init: RequestInit = signal === undefined
        ? { credentials: "include" }
        : { credentials: "include", signal };
      try {
        const res = await fetch(url, init);
        if (!res.ok) {
          throw new Error(`integrations fetch failed (${res.status})`);
        }
        const raw = (await res.json()) as ReadonlyArray<
          Parameters<typeof fromApi>[0]
        >;
        const mapped = raw.map(fromApi);
        setIntegrations(mapped);
        setStatus("ready");
        setError(null);
        lastFetchAt.current = Date.now();
        writeUserCache(integrationsResource(tenantId), mapped);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    // Re-seed on tenant switch: cached data for the new tenant renders
    // immediately while the fetch revalidates; a miss falls back to the
    // skeleton-backed loading state.
    setIntegrations(cached ?? []);
    setStatus(cached !== null ? "ready" : "loading");
    setError(null);
    void fetchIntegrations(controller.signal);
    return () => controller.abort();
  }, [fetchIntegrations, cached]);

  const refetch = useCallback((): Promise<void> => fetchIntegrations(), [fetchIntegrations]);

  // Connecting an integration happens in ANOTHER tab (the OAuth hand-off), so
  // the tab holding this data has no way to learn the connection landed — it
  // fetched once on mount and would sit on "Connect" until a manual reload.
  // Coming back to this tab is the signal. Same throttled focus revalidation
  // useCachedQuery gives the admin views; the write-through paths above keep
  // their own state, so this only ever tops up what's already on screen.
  useEffect(() => {
    if (tenantId === null) return;
    const onFocus = (): void => {
      if (document.visibilityState === "hidden") return;
      if (!shouldRefetchOnFocus(lastFetchAt.current, Date.now())) return;
      void fetchIntegrations();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [fetchIntegrations, tenantId]);

  const request = useCallback(
    async (slug: string): Promise<RequestResult> => {
      if (tenantId === null) {
        return {
          ok: false,
          code: "UNKNOWN",
          message: "No active instance to request against.",
        };
      }
      try {
        const res = await fetch(
          `/api/integrations/${encodeURIComponent(tenantId)}/${encodeURIComponent(slug)}/request`,
          {
            method: "POST",
            credentials: "include",
            headers: { ...getCsrfHeader() },
          },
        );

        if (res.ok) {
          const raw = (await res.json()) as Parameters<typeof fromApi>[0];
          const integration = fromApi(raw);
          setIntegrations((prev) => {
            const next = prev.slice();
            const idx = next.findIndex((i) => i.slug === integration.slug);
            if (idx === -1) {
              next.push(integration);
            } else {
              next[idx] = integration;
            }
            writeUserCache(integrationsResource(tenantId), next);
            return next;
          });
          return { ok: true, integration };
        }

        if (res.status === 401) {
          return {
            ok: false,
            code: "UNAUTHORIZED",
            message: "Session expired. Refresh to sign in again.",
          };
        }

        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isRequestErrorCode(code) ? code : "UNKNOWN";
        return {
          ok: false,
          code: narrowed,
          message: body?.message ?? `Request failed (${res.status})`,
        };
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        return { ok: false, code: "UNKNOWN", message };
      }
    },
    [tenantId],
  );

  return { status, integrations, error, request, refetch };
};
